import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchReportTypes,
  updateReportType,
  createReportType,
  deleteReportType,
  fetchReportType,
} from "../../api/service/settings";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  report_type: {},
};

export const reportTypesController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteReportTypeAsync = createAsyncThunk(
  "report_types/deleteReportTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteReportType(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_report_type");
    }
  }
);

export const createReportTypeAsync = createAsyncThunk(
  "report_types/createReportTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createReportType(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_report_type");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_report_type");
    }
  }
);

export const updateReportTypeAsync = createAsyncThunk(
  "report_types/updateReportTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateReportType(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_report_type");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_report_type");
    }
  }
);

export const getReportTypes = createAsyncThunk(
  "report_types/getReportTypes",
  async (controller = reportTypesController, { rejectWithValue }) => {
    try {
      const response = await fetchReportTypes(controller);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "list_report_types");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_report_types");
    }
  }
);

export const getReportTypeAsync = createAsyncThunk(
  "products/getReportTypeAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchReportType(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_report_type");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_report_type");
    }
  }
);

const reportTypeSlice = createSlice({
  name: "report_types",
  initialState,
  reducers: {
    resetReportType(state) {
      state.report_type = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReportTypes.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getReportTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
      })
      .addCase(getReportTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente rapporttypene."
        );
      })
      .addCase(updateReportTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateReportTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.payload?.id;
          const index = draftState.data.findIndex((type) => type.id === id);
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateReportTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere rapporttypen."
        );
      })
      .addCase(createReportTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createReportTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createReportTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette rapporttypen."
        );
      })
      .addCase(deleteReportTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteReportTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteReportTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette rapporttypen."
        );
      })
      .addCase(getReportTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.report_type = {};
        state.error = false;
        state.errors = null;
      })
      .addCase(getReportTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.report_type = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getReportTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.report_type = {};
        state.errors = rejectedErrors(action, "Kunne ikke hente rapporttypen.");
      });
  },
});

export const { reducer } = reportTypeSlice;

export const { resetReportType } = reportTypeSlice.actions;

export default reportTypeSlice;

export const selectReportTypeById = (state, reportTypeId) =>
  state.report_types.data.find((type) => type.id == reportTypeId);
